import React, { useState, useEffect } from 'react';
import { Initiative, InitiativeDashboardKPIs } from './types';
import { initiativesService } from './initiativesService';
import { InitiativesMetricsCards } from './InitiativesMetricsCards';
import { InitiativesTable } from './InitiativesTable';
import { InitiativeDetailModal } from './InitiativeDetailModal';
import { InitiativeCreateModal } from './InitiativeCreateModal';

export const InitiativesPage: React.FC = () => {
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [kpis, setKpis] = useState<InitiativeDashboardKPIs | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [healthFilter, setHealthFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');

  // Modals
  const [selectedInitiative, setSelectedInitiative] = useState<Initiative | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState<boolean>(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [list, stats] = await Promise.all([
        initiativesService.getInitiatives({
          search: search || undefined,
          status: statusFilter || undefined,
          health_status: healthFilter || undefined,
          initiative_type: typeFilter || undefined
        }),
        initiativesService.getDashboardKPIs()
      ]);
      setInitiatives(list);
      setKpis(stats);
      if (selectedInitiative) {
        const reloaded = list.find(i => i.id === selectedInitiative.id);
        if (reloaded) setSelectedInitiative(reloaded);
      }
    } catch (err: any) {
      setError(err.message || 'Veri yuklenirken hata olustu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [search, statusFilter, healthFilter, typeFilter]);

  const handleCreate = async (data: Partial<Initiative>) => {
    await initiativesService.createInitiative(data);
    await loadData();
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Top Header */}
      <div className="page-header-banner">
        <div className="badge">STRATEGIC GOVERNANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Strategic Initiatives & Coordination</h1>
            <p>Corporate goals, workstreams, milestone tracking, central action integration, and formal closure signoffs.</p>
          </div>
          <button
            onClick={() => setIsCreateOpen(true)}
            className="btn-primary"
            style={{ width: 'auto', padding: '10px 18px' }}
          >
            + New Strategic Initiative
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <InitiativesMetricsCards kpis={kpis} />

      {/* Search & Filter Bar */}
      <div style={{
        background: '#ffffff',
        padding: '16px',
        borderRadius: '8px',
        border: '1px solid #e2e8f0',
        marginBottom: '20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
        alignItems: 'center'
      }}>
        <div style={{ flex: '1 1 260px' }}>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by initiative title, number, or department..."
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', boxSizing: 'border-box' }}
          />
        </div>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
        >
          <option value="">All Statuses</option>
          <option value="DRAFT">Draft</option>
          <option value="SUBMITTED">Submitted</option>
          <option value="APPROVED">Approved</option>
          <option value="ACTIVE">Active Execution</option>
          <option value="ON_HOLD">On Hold</option>
          <option value="UNDER_REVIEW">Under Review</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>

        <select
          value={healthFilter}
          onChange={(e) => setHealthFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
        >
          <option value="">All Health Statuses</option>
          <option value="ON_TRACK">On Track</option>
          <option value="AT_RISK">At Risk</option>
          <option value="CRITICAL">Critical</option>
        </select>

        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px', background: '#fff' }}
        >
          <option value="">All Initiative Types</option>
          <option value="STRATEGIC">Strategic Initiative</option>
          <option value="SAFETY_IMPROVEMENT">Safety Improvement</option>
          <option value="QUALITY_EXCELLENCE">Quality Excellence</option>
          <option value="OPERATIONAL_EFFICIENCY">Operational Efficiency</option>
          <option value="COMPLIANCE_MANDATE">Compliance Mandate</option>
          <option value="DIGITAL_TRANSFORMATION">Digital Transformation</option>
        </select>

        <button
          onClick={() => {
            setSearch('');
            setStatusFilter('');
            setHealthFilter('');
            setTypeFilter('');
          }}
          style={{ background: '#f1f5f9', color: '#475569', border: 'none', padding: '8px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}
        >
          Reset Filters
        </button>
      </div>

      {/* Error state */}
      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #f87171', color: '#b91c1c', padding: '12px 16px', borderRadius: '8px', marginBottom: '20px', fontSize: '13px' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <InitiativesTable
        initiatives={initiatives}
        selectedId={selectedInitiative?.id || null}
        onSelect={(init) => setSelectedInitiative(init)}
      />

      {/* Modals */}
      {selectedInitiative && (
        <InitiativeDetailModal
          initiative={selectedInitiative}
          onClose={() => setSelectedInitiative(null)}
          onRefresh={loadData}
        />
      )}

      <InitiativeCreateModal
        isOpen={isCreateOpen}
        onClose={() => setIsCreateOpen(false)}
        onSubmit={handleCreate}
      />
    </div>
  );
};
